import { X } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { scalarOf } from './filterRowValue';
import { Input } from './inputs/Input';
import { SearchableSelect } from './inputs/SearchableSelect';
import { Select } from './inputs/Select';

export type Operator<T> = {
  label: string;
  operator: T;
};

export type FilterFieldOption<TOperation = string> = {
  field: string;
  label: string;
  operatorOptions: Operator<TOperation>[];
  inputType: 'string' | 'number' | 'select' | 'searchableSelect' | 'date';
  selectOptions?: { label: string; value: string }[];
  withAllOption?: boolean;
};

export type FilterRowValue = string | number | string[];

export type FilterValueInputRenderer = (
  value: FilterRowValue,
  onChange: (next: FilterRowValue) => void,
  name: string
) => ReactNode;

interface FilterRowProps {
  field: string;
  operator: string;
  value: FilterRowValue;
  connector: 'and' | 'or';
  index: number;
  fieldOptions: FilterFieldOption[];
  whereLabel: string;
  andLabel: string;
  orLabel: string;
  removeLabel: string;
  fixedConnectorLabel?: string;
  renderValueInput?: FilterValueInputRenderer;
  onFieldChange: (index: number, value: string) => void;
  onOperatorChange: (index: number, value: string) => void;
  onValueChange: (index: number, value: FilterRowValue) => void;
  onConnectorChange: (index: number, connector: 'and' | 'or') => void;
  onRemove: (index: number) => void;
}

const valueInputTypes = {
  number: 'number',
  date: 'date',
  string: 'text',
  select: 'text',
} as const;

export function FilterRow({
  field,
  operator,
  value,
  connector,
  index,
  fieldOptions,
  whereLabel,
  andLabel,
  orLabel,
  removeLabel,
  fixedConnectorLabel,
  renderValueInput,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onConnectorChange,
  onRemove,
}: FilterRowProps) {
  const option = fieldOptions.find((opt) => opt.field === field);

  const fieldSelectOptions = useMemo(
    () => fieldOptions.map((opt) => ({ label: opt.label, value: opt.field })),
    [fieldOptions]
  );

  const operatorSelectOptions = useMemo(
    () =>
      (option?.operatorOptions ?? []).map((op) => ({
        label: op.label,
        value: String(op.operator),
      })),
    [option]
  );

  const selectionOptions = useMemo(() => {
    const base = option?.selectOptions ?? [];
    return option?.withAllOption
      ? [{ label: `All ${option.label}`, value: '' }, ...base]
      : base;
  }, [option]);

  const scalarValue = scalarOf(value);
  const connectorLabel =
    fixedConnectorLabel ?? (index === 0 ? whereLabel : undefined);

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2 mb-1">
          {connectorLabel !== undefined ? (
            <span className="text-xs font-medium text-neut-sec">
              {connectorLabel}
            </span>
          ) : (
            <div className="flex rounded border border-neut-border overflow-hidden">
              <button
                onClick={() => onConnectorChange(index, 'and')}
                className={`px-2 py-0.5 text-xs font-medium transition-colors ${
                  connector === 'and'
                    ? 'bg-prim text-white'
                    : 'bg-neut-0 text-neut-sec hover:bg-neut-50'
                }`}
              >
                {andLabel}
              </button>
              <button
                onClick={() => onConnectorChange(index, 'or')}
                className={`px-2 py-0.5 text-xs font-medium transition-colors border-l border-neut-border ${
                  connector === 'or'
                    ? 'bg-prim text-white'
                    : 'bg-neut-0 text-neut-sec hover:bg-neut-50'
                }`}
              >
                {orLabel}
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Select
            name={`field-${index}`}
            value={field}
            options={fieldSelectOptions}
            onChange={(_, v) => onFieldChange(index, v)}
            containerClassName="flex-1 min-w-0"
          />
          <Select
            name={`operator-${index}`}
            value={operator}
            options={operatorSelectOptions}
            onChange={(_, v) => onOperatorChange(index, v)}
            containerClassName="flex-1"
          />
        </div>
        <div className="flex items-start gap-2">
          {renderValueInput ? (
            renderValueInput(
              value,
              (v) => onValueChange(index, v),
              `value-${index}`
            )
          ) : option?.inputType === 'select' ? (
            <Select
              name={`value-${index}`}
              value={String(scalarValue) || ''}
              options={selectionOptions}
              containerClassName="flex-1 min-w-0"
              onChange={(_, v) => onValueChange(index, v)}
            />
          ) : option?.inputType === 'searchableSelect' ? (
            <SearchableSelect
              name={`value-${index}`}
              value={String(scalarValue) || ''}
              options={selectionOptions}
              containerClassName="flex-1 min-w-0"
              onChange={(_, v) => onValueChange(index, v)}
            />
          ) : (
            <Input
              type={valueInputTypes[option?.inputType ?? 'string']}
              name={`value-${index}`}
              value={scalarValue}
              containerClassName="flex-1"
              onChange={(_, v) => onValueChange(index, v)}
            />
          )}
          <button
            className="shrink-0 p-2 text-neut-sec hover:text-error-600 hover:bg-error-100 rounded-lg transition-colors"
            title={removeLabel}
            onClick={() => onRemove(index)}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
